import { useState, useEffect, useRef } from "react";
import { useDebounce } from "./use-debounce";

export function useSync(syncFunction: () => Promise<void>, intervalMs = 3000) {
  const [isSyncing, setIsSyncing] = useState(false);
  const lastSyncTime = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const pendingSyncRef = useRef<boolean>(false); // 대기 중인 동기화 요청 추적

  // 디바운스된 즉시 동기화 함수 (중복 호출 방지)
  const debouncedSync = useDebounce(async () => {
    await syncNow();
  }, 300); // 300ms 디바운스

  // 동기화 함수
  const syncNow = async () => {
    // 이미 동기화 중이면 대기 상태로 표시하고 종료
    if (isSyncing) {
      pendingSyncRef.current = true;
      console.log("Sync already in progress, marking pending request");
      return;
    }
    try {
      setIsSyncing(true);
      await syncFunction();
      lastSyncTime.current = Date.now();
    } catch (error) {
      console.error("Sync error:", error);
    } finally {
      setIsSyncing(false);
      // 동기화 완료 후 대기 중인 요청이 있으면 즉시 실행
      if (pendingSyncRef.current) {
        pendingSyncRef.current = false;
        // setTimeout을 사용하여 비동기적으로 실행
        setTimeout(() => {
          syncNow();
        }, 0);
      }
    }
  };

  // 정기적인 동기화 설정
  useEffect(() => {
    // 컴포넌트 마운트 시 초기 동기화
    syncNow();

    // 정기적 동기화 인터벌 설정
    intervalRef.current = setInterval(async () => {
      // 이미 동기화 중이면 건너뛰기
      if (isSyncing) {
        return;
      }

      // 마지막 동기화 후 충분한 시간이 지났는지 확인 (throttle 효과)
      const timeSinceLastSync = Date.now() - lastSyncTime.current;
      if (timeSinceLastSync >= intervalMs * 0.8) {
        // 간격의 80% 이상 지났을 때만 실행
        await syncNow();
      }
    }, intervalMs);

    // 클린업
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [intervalMs]);

  return {
    isSyncing,
    syncNow: debouncedSync, // 디바운스된 함수 반환
    lastSyncTime: () => lastSyncTime.current,
  };
}
